import { useCurrentUser } from 'components/providers'
import { isEqual } from 'lodash-es'
import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { ITemplate } from 'types'
import { deleteDoc, removeEmpty, setDoc, useBeforeUnload, useDoc } from 'utils'
import { useMutateTemplateRecords } from 'utils/templateUtils'
import { cleanTemplate } from '../../utils/templateBuilderUtils'

interface TemplateContextValue {
  template: ITemplate | null
  isDirty: boolean
  updateTemplate: (update: Partial<ITemplate>) => void
  saveTemplate: () => Promise<void>
  deleteTemplate: () => Promise<void>
  resetTemplate: () => void
}

export const useTemplate = () => useContext(TemplateContext)

const TemplateContext = createContext<TemplateContextValue>({} as TemplateContextValue)

export function TemplateProvider({ children }: { children: ReactNode }) {
  const { id } = useParams()
  const user = useCurrentUser()
  const { updateTemplateRecord, removeTemplateRecord } = useMutateTemplateRecords()
  const [template] = useDoc('templates', id)
  const [draft, setDraft] = useState<ITemplate | null>()

  const isDirty = Boolean(draft) && !isEqual(cleanTemplate(draft), cleanTemplate(template))
  useBeforeUnload(isDirty)

  useEffect(() => {
    setDraft(template)
  }, [template])

  function updateTemplate(update: Partial<ITemplate>) {
    setDraft(s => (s ? removeEmpty({ ...s, ...update }) : null))
  }

  async function saveTemplate() {
    const cleaned = cleanTemplate(draft)

    if (cleaned && user) {
      const updated = await setDoc(user.id, 'templates', cleaned)
      updateTemplateRecord(updated)
    }
  }

  async function deleteTemplate() {
    if (!user || !template) return
    await deleteDoc('templates', template.id)
    removeTemplateRecord(template.id)
  }

  function resetTemplate() {
    setDraft(template)
  }

  const value: TemplateContextValue = {
    template: draft ?? null,
    isDirty,
    updateTemplate,
    saveTemplate,
    deleteTemplate,
    resetTemplate,
  }

  return <TemplateContext.Provider value={value}>{children}</TemplateContext.Provider>
}
